// Abas de conteúdo (DESIGN §5.9): traço tinta sob a ativa, rola na horizontal, setas do teclado movem.
// Para filtros de uma mesma lista, prefira chips.
import { useId, useRef, type ReactNode } from "react";
import { cn } from "../lib/cn";

export interface Aba<V extends string = string> {
  valor: V;
  rotulo: ReactNode;
  /** Contador opcional ao lado do rótulo. */
  contagem?: number;
}

export interface PropsAbas<V extends string> {
  rotulo: string;
  abas: Aba<V>[];
  valor: V;
  aoMudar: (v: V) => void;
  className?: string;
  /** Prefixo de id para ligar aba e painel (`${idBase}-painel-${valor}`). */
  idBase?: string;
}

export function Abas<V extends string>({ rotulo, abas, valor, aoMudar, className, idBase }: PropsAbas<V>) {
  const auto = useId();
  const base = idBase ?? auto;
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const mover = (delta: number) => {
    const i = abas.findIndex((a) => a.valor === valor);
    const j = (i + delta + abas.length) % abas.length;
    const prox = abas[j];
    if (prox) {
      aoMudar(prox.valor);
      refs.current[j]?.focus();
    }
  };

  return (
    <div
      role="tablist"
      aria-label={rotulo}
      className={cn("no-scrollbar flex gap-6 overflow-x-auto border-b border-fio", className)}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") {
          e.preventDefault();
          mover(1);
        }
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          mover(-1);
        }
      }}
    >
      {abas.map((a, i) => {
        const ativa = a.valor === valor;
        return (
          <button
            key={a.valor}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`${base}-aba-${a.valor}`}
            aria-selected={ativa}
            aria-controls={`${base}-painel-${a.valor}`}
            tabIndex={ativa ? 0 : -1}
            onClick={() => aoMudar(a.valor)}
            className={cn(
              "relative h-12 shrink-0 whitespace-nowrap text-apoio",
              ativa ? "font-semibold text-tinta" : "font-medium text-texto-2 hover:text-tinta",
            )}
          >
            {a.rotulo}
            {a.contagem !== undefined && (
              <span className="ml-1.5 text-rotulo text-texto-3 placar">{a.contagem}</span>
            )}
            {ativa && <span aria-hidden className="absolute inset-x-0 bottom-0 h-[3px] rounded-t bg-tinta" />}
          </button>
        );
      })}
    </div>
  );
}

/** Painel de conteúdo ligado a uma aba. */
export function PainelAba({
  idBase,
  valor,
  children,
  className,
}: {
  idBase: string;
  valor: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      role="tabpanel"
      id={`${idBase}-painel-${valor}`}
      aria-labelledby={`${idBase}-aba-${valor}`}
      className={className}
    >
      {children}
    </div>
  );
}
